// The time of recording of a video (PRD-0074, slice 06): what the document shows, where it came
// from, and the other readings the analysis made — plus the clock the player's strip reads from
// it. Pure; the caption and the strip under `components/` only draw what these answer.
import type {
  RecordedAtCandidates,
  RecordedAtSource,
  VideoPlaybackResponse,
} from '@/services/api/video'
import { formatClockTime } from '@/utils/date'

export interface RecordedTime {
  /** When the recording starts (ISO 8601), null when nothing says. */
  time: string | null
  /** Where the time came from: an operator, the stamp in the picture, the file's own tag. */
  source: RecordedAtSource | null
  candidates: RecordedAtCandidates
}

export const UNKNOWN_RECORDED_TIME: RecordedTime = {
  time: null,
  source: null,
  candidates: { burned_in: null, metadata: null, operator: null },
}

/** The time of recording as the playback read carries it; unknown before the read or without one. */
export function recordedTimeOf(playback: VideoPlaybackResponse | null | undefined): RecordedTime {
  if (!playback) return UNKNOWN_RECORDED_TIME
  return {
    time: playback.recorded_at,
    source: playback.recorded_at_source,
    candidates: playback.recorded_at_candidates,
  }
}

/** Two ISO times name the same moment, whatever their offsets' spelling. */
export function sameMoment(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  const left = new Date(a).getTime()
  const right = new Date(b).getTime()
  return !Number.isNaN(left) && left === right
}

/** A reading of the analysis that differs from the time shown — what the caption adds in words. */
export interface OtherReading {
  source: 'burned_in' | 'metadata'
  time: string
}

/**
 * The analysis's readings that disagree with the time shown, stamp first: on a CCTV export the
 * file carries the export time and the picture the camera's, and the page says both.
 */
export function otherReadings(recording: RecordedTime): OtherReading[] {
  const out: OtherReading[] = []
  for (const source of ['burned_in', 'metadata'] as const) {
    const time = recording.candidates[source]
    if (time && source !== recording.source && !sameMoment(time, recording.time)) {
      out.push({ source, time })
    }
  }
  return out
}

/** Whether the analysis found a time of its own — what clearing an operator's time falls back to. */
export function hasAnalysisTime(candidates: RecordedAtCandidates): boolean {
  return Boolean(candidates.burned_in || candidates.metadata)
}

/** The time of day `t` seconds into a recording that starts at `recordedAt`; null without a start. */
export function wallClockAt(recordedAt: string | null, t: number): Date | null {
  if (!recordedAt) return null
  const start = new Date(recordedAt).getTime()
  if (Number.isNaN(start)) return null
  return new Date(start + Math.max(0, t) * 1000)
}

/** A stretch of a recording on the camera's clock, `07:15:47 – 07:16:55`; null without a start. */
export function wallSpan(
  recordedAt: string | null,
  from: number,
  to: number,
  locale: string
): string | null {
  const start = wallClockAt(recordedAt, Math.floor(from))
  const end = wallClockAt(recordedAt, Math.floor(to))
  if (!start || !end) return null
  return `${formatClockTime(start, locale)} – ${formatClockTime(end, locale)}`
}
